import type { SneakPeekDay, SneakPeekLesson } from '@/services/api/sneakPeek'
import { MARKS } from '@/pages/sneak-peek/marks'

function germanDate(iso: string) {
  const [year, month, day] = iso.slice(0, 10).split('-')
  return `${day}.${month}.${year}`
}

function rangeLabel(from: string, to: string) {
  if (from === to) return germanDate(from)
  return `${germanDate(from)} – ${germanDate(to)}`
}

function clock(start: string | null) {
  return start ? start.slice(0, 5) : ''
}

function lessonLine(lesson: SneakPeekLesson) {
  return [clock(lesson.start_time), lesson.klass_name, lesson.subject_name, lesson.room_name]
    .filter(Boolean)
    .join(' ')
}

function wholeDayNames(day: SneakPeekDay) {
  const listed = (day.whole_day_names ?? []).filter(Boolean)
  if (listed.length) return [...new Set(listed)]
  return day.whole_day_name ? [day.whole_day_name] : []
}

function combinationLessons(day: SneakPeekDay) {
  const ids = new Set<string>()
  for (const row of day.packages || []) {
    for (const id of row.lesson_ids) ids.add(id)
  }
  for (const id of day.open_lesson_ids || []) ids.add(id)
  return (day.lessons || [])
    .filter((lesson) => ids.has(lesson.entry_id))
    .sort((left, right) => (left.start_time || '').localeCompare(right.start_time || ''))
}

export function SneakPeekPrintView({
  absent,
  from,
  to,
  threshold,
  selectedDates,
  days,
}: {
  absent: string
  from: string
  to: string
  threshold: number
  selectedDates: string[]
  days: SneakPeekDay[]
}) {
  const visible = days.filter((day) => !day.empty)
  return (
    <section aria-label="Druck">
      <p>{absent}</p>
      <p>{rangeLabel(from, to)}</p>
      <p>Schwelle {threshold}</p>
      {selectedDates.length > 0 && (
        <p>Gewählte Tage: {selectedDates.map(germanDate).join(', ')}</p>
      )}
      {visible.map((day) => {
        const whole = day.combination === MARKS.wholeDay
        const lessons = combinationLessons(day)
        const people = (day.packages || []).map((row) => row.name)
        const names = whole ? wholeDayNames(day) : []
        return (
          <article key={day.date} aria-label={`Druck ${day.date}`}>
            <p>{germanDate(day.date)}</p>
            {whole && <p>{MARKS.wholeDay}</p>}
            {lessons.map((lesson) => <p key={lesson.entry_id}>{lessonLine(lesson)}</p>)}
            {names.map((name) => <p key={name}>{name}</p>)}
            {people.filter((name) => !names.includes(name)).map((name) => {
              const row = (day.packages || []).find((item) => item.name === name)
              const clocks = (row?.lesson_ids || [])
                .map((id) => lessons.find((lesson) => lesson.entry_id === id))
                .filter((lesson): lesson is SneakPeekLesson => lesson != null)
                .map((lesson) => clock(lesson.start_time))
                .filter(Boolean)
              return <p key={name}>{clocks.length ? `${name} ${clocks.join('+')}` : name}</p>
            })}
            {!whole && (day.open_lesson_ids || []).length > 0 && <p>offen</p>}
            {day.mark && <p>{day.mark}</p>}
          </article>
        )
      })}
    </section>
  )
}
